/**
 * Frameworks tab state and actions for the school management screen (ADM-8):
 * list, upload with live extraction tracking, delete, and the editor modal
 * selection. Also loads the frameworks a document upload can link to.
 */
import React, { useState } from 'react';

import { confirmDialog, showDialog } from '@/utils/dialogs';
import type { ToastType } from '@/components/ui/Toast';
import { apiRequest } from '../../services/apiClient';
import { useTaskEvents, useWebSocketConnection } from '../../hooks/useTaskProgress';
import type { FrameworkListResponse, SchoolFramework } from '../../types/schoolFramework';
import { getSchoolApiBase } from './schoolApiBase';
import type { School, SchoolManagementTab } from './schoolManagementTypes';

type TFn = (key: string, defaultOrVars?: string | Record<string, any>, vars?: Record<string, any>) => string;

export function useSchoolFrameworks({
  school,
  canAccessPrivateSchoolKnowledge,
  setActiveTab,
  reloadDocuments,
  t,
  showToast,
}: {
  school: School | null;
  canAccessPrivateSchoolKnowledge: boolean;
  setActiveTab: (tab: SchoolManagementTab) => void;
  /** Deleting a framework also refreshes the documents linked to it. */
  reloadDocuments: () => Promise<void>;
  t: TFn;
  showToast: (message: string, type?: ToastType) => void;
}) {
  const { subscribe, unsubscribe, getTaskProgress } = useTaskEvents();
  const {
    isConnected: isTaskSocketConnected,
    useFallback: taskSocketFallback,
  } = useWebSocketConnection();

  const [frameworks, setFrameworks] = useState<SchoolFramework[]>([]);
  const [loadingFrameworks, setLoadingFrameworks] = useState(false);
  const [showFrameworkEditor, setShowFrameworkEditor] = useState(false);
  const [selectedFrameworkId, setSelectedFrameworkId] = useState<number | null>(null);
  const [uploadingFramework, setUploadingFramework] = useState(false);
  const [deletingFrameworkId, setDeletingFrameworkId] = useState<number | null>(null);

  const loadFrameworks = React.useCallback(async (): Promise<SchoolFramework[]> => {
    if (!school || !canAccessPrivateSchoolKnowledge) {
      setFrameworks([]);
      return [];
    }
    try {
      setLoadingFrameworks(true);
      const data: FrameworkListResponse = await apiRequest({
        endpoint: `/school-documents/${school.id}/frameworks`,
        method: 'GET',
      });
      const nextFrameworks = data.frameworks || [];
      setFrameworks(nextFrameworks);
      return nextFrameworks;
    } catch (error) {
      console.error('Error loading frameworks:', error);
      return [];
    } finally {
      setLoadingFrameworks(false);
    }
  }, [canAccessPrivateSchoolKnowledge, school]);

  const waitForFrameworkTaskWebSocket = React.useCallback(
    (taskId: string): Promise<{ status: 'SUCCESS' | 'FAILURE'; result?: any; error?: string }> => {
      if (!isTaskSocketConnected || taskSocketFallback) {
        return Promise.reject(new Error('Live framework extraction channel is unavailable.'));
      }

      return new Promise((resolve, reject) => {
        const timeoutMs = 5 * 60_000;
        const timeoutAt = Date.now() + timeoutMs;

        const cleanup = (intervalId: ReturnType<typeof setInterval>) => {
          clearInterval(intervalId);
          unsubscribe(taskId);
        };

        subscribe(taskId);

        const intervalId = setInterval(() => {
          const progress = getTaskProgress(taskId);

          if (progress?.error) {
            cleanup(intervalId);
            resolve({ status: 'FAILURE', error: progress.error });
            return;
          }

          if (progress?.isComplete) {
            cleanup(intervalId);
            resolve({ status: 'SUCCESS', result: progress.result });
            return;
          }

          if (Date.now() >= timeoutAt) {
            cleanup(intervalId);
            reject(new Error('Framework extraction timed out.'));
          }
        }, 250);
      });
    },
    [getTaskProgress, isTaskSocketConnected, subscribe, taskSocketFallback, unsubscribe]
  );

  const handleUploadFramework = async (language: string = 'tr') => {
    if (!school) return;

    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.pdf,.txt,.docx';
    input.onchange = async (e: any) => {
      const file = e.target?.files?.[0];
      if (!file) return;

      try {
        setUploadingFramework(true);
        const API = getSchoolApiBase();
        const { getAccessToken, USE_COOKIES } = await import('../../services/api');
        const token = USE_COOKIES ? null : await getAccessToken();

        const formData = new FormData();
        formData.append('file', file);
        formData.append('coaching_method', 'custom_framework');
        formData.append('is_framework', 'true');
        formData.append('framework_language', language);

        const response = await fetch(
          `${API}/api/school-documents/${school.id}/documents/upload`,
          {
            method: 'POST',
            credentials: USE_COOKIES ? 'include' : 'same-origin',
            headers: token ? { 'Authorization': `Bearer ${token}` } : {},
            body: formData,
          }
        );

        if (!response.ok) throw new Error('Upload failed');
        const payload = await response.json().catch(() => null);
        const uploadedDocumentId = Number(payload?.id);
        const extractionTaskId = typeof payload?.framework_extraction_task_id === 'string'
          ? payload.framework_extraction_task_id
          : null;
        setActiveTab('frameworks');
        showToast(t('schoolManagement.frameworksSection.extractionInProgress'), 'info');
        let availableFrameworks = await loadFrameworks();

        if (extractionTaskId) {
          let taskResult: { status: 'SUCCESS' | 'FAILURE'; result?: any; error?: string };
          try {
            taskResult = await waitForFrameworkTaskWebSocket(extractionTaskId);
          } catch (taskWaitError) {
            console.error('Error waiting for framework extraction task:', taskWaitError);
            showDialog(t('common.info'), t('schoolManagement.frameworksSection.reviewTimeout'));
            return;
          }

          if (taskResult.status !== 'SUCCESS') {
            showDialog(t('common.error'), taskResult.error || t('schoolManagement.frameworksSection.uploadError'));
            return;
          }

          availableFrameworks = await loadFrameworks();
          const frameworkId = Number(taskResult.result?.framework_id);
          const extracted = Number.isFinite(frameworkId) && frameworkId > 0
            ? availableFrameworks.find((fw) => fw.id === frameworkId) || null
            : null;

          if (extracted) {
            showDialog(t('common.success'), t('schoolManagement.frameworksSection.reviewReady'));
            setSelectedFrameworkId(extracted.id);
            setShowFrameworkEditor(true);
            return;
          }
        }

        if (Number.isFinite(uploadedDocumentId) && uploadedDocumentId > 0) {
          availableFrameworks = await loadFrameworks();
          const extracted = availableFrameworks.find((fw) => fw.source_document_id === uploadedDocumentId) || null;
          if (extracted) {
            showDialog(t('common.success'), t('schoolManagement.frameworksSection.reviewReady'));
            setSelectedFrameworkId(extracted.id);
            setShowFrameworkEditor(true);
          } else {
            showDialog(t('common.info'), t('schoolManagement.frameworksSection.reviewTimeout'));
          }
        }
      } catch (error) {
        console.error('Error uploading framework:', error);
        showDialog(t('common.error'), t('schoolManagement.frameworksSection.uploadError'));
      } finally {
        setUploadingFramework(false);
      }
    };
    input.click();
  };

  // Frameworks a document can be linked to (loaded on demand for the upload sheet).
  const loadLinkableFrameworks = async (): Promise<SchoolFramework[]> => {
    if (!school) return [];
    if (frameworks.length) return frameworks;
    try {
      const data: FrameworkListResponse = await apiRequest({
        endpoint: `/school-documents/${school.id}/frameworks`,
        method: 'GET',
      });
      const available = data.frameworks || [];
      setFrameworks(available);
      return available;
    } catch (error) {
      console.error('Error loading frameworks for document linking:', error);
      return [];
    }
  };

  const handleDeleteFramework = async (frameworkId: number, frameworkName: string) => {
    if (!school) return;

    const confirmationMessage = t('schoolManagement.frameworksSection.deleteConfirm', {
      name: frameworkName,
    });
    const confirmed = await confirmDialog(
      t('schoolManagement.frameworksSection.deleteTitle'),
      confirmationMessage,
      t('common.delete'),
      t('common.cancel'),
    );

    if (!confirmed) return;

    try {
      setDeletingFrameworkId(frameworkId);
      await apiRequest({
        endpoint: `/school-documents/${school.id}/frameworks/${frameworkId}`,
        method: 'DELETE',
      });

      if (selectedFrameworkId === frameworkId) {
        setShowFrameworkEditor(false);
        setSelectedFrameworkId(null);
      }

      showToast(t('schoolManagement.frameworksSection.deleteSuccess'), 'success');
      await Promise.all([loadFrameworks(), reloadDocuments()]);
    } catch (error) {
      console.error('Error deleting framework:', error);
      showDialog(t('common.error'), t('schoolManagement.frameworksSection.deleteError'));
    } finally {
      setDeletingFrameworkId(null);
    }
  };

  const openFrameworkEditor = (frameworkId: number) => {
    setSelectedFrameworkId(frameworkId);
    setShowFrameworkEditor(true);
  };

  const closeFrameworkEditor = () => {
    setShowFrameworkEditor(false);
    setSelectedFrameworkId(null);
  };

  return {
    frameworks,
    loadingFrameworks,
    showFrameworkEditor,
    selectedFrameworkId,
    uploadingFramework,
    deletingFrameworkId,
    loadFrameworks,
    loadLinkableFrameworks,
    handleUploadFramework,
    handleDeleteFramework,
    openFrameworkEditor,
    closeFrameworkEditor,
  };
}

export type SchoolFrameworksState = ReturnType<typeof useSchoolFrameworks>;
